import React from 'react';
import { RSInput, RSCheckbox } from 'reactsymbols-kit';
import MerlinSelect, { type MerlinSelectOption } from '../MerlinSelect';

/** The model-settings notation choices. */
type Notation = 'MERODE' | 'UML';

/**
 * The settings this form edits. Each change is delivered to `onChange` as a
 * partial object (one key at a time).
 */
export interface ModelSettings {
  name: string;
  notation: Notation;
  generateMethods: boolean;
  propagateOnDependency: boolean;
  propagateOnMethod: boolean;
}

export interface ModelSettingsFormProps {
  values: ModelSettings;
  /** On change handler. Change is passed as a partial settings object. */
  onChange: (change: Partial<ModelSettings>) => void;
  /** Set to true to disable the inputs. */
  disabled?: boolean;
}

// reactsymbols-kit's RSInput/RSCheckbox extend React.HTMLProps, so their
// declared `onChange` is the unusable intersection of the DOM FormEventHandler
// with the kit's value callback. Route the real value handler through this
// helper, re-typed as the shape each component's prop wants (the single
// library-boundary escape for this kit bug; the runtime handler is unchanged).
type RSInputChange = React.ComponentProps<typeof RSInput>['onChange'];
type RSCheckboxChange = React.ComponentProps<typeof RSCheckbox>['onChange'];
function asRSChange<T>(handler: (...args: never[]) => void): T {
  return handler as unknown as T;
}

/**
 * The options for the model notation.
 */
const notationOptions = [
  {
    label: 'Use MERODE notation',
    value: 'MERODE',
  },
  {
    label: 'Use UML notation',
    value: 'UML',
  },
];

/**
 * Render a controlled form for the model settings.
 */
const ModelSettingsForm = ({ values, onChange, disabled }: ModelSettingsFormProps) => (
  <React.Fragment>
    <div className="row model-name">
      <div className="col-2">
        <RSInput
          name="name"
          label="Give your model a name"
          value={values.name}
          onChange={asRSChange<RSInputChange>((value: string) => onChange({ name: value }))}
          disabled={disabled}
          autoFocus={true}
        />
      </div>
      <div className="col-2">
        <div className="rs-element rs-select">
          <label className="rs-element rs-form-label">Choose your notation style</label>
          <MerlinSelect
            options={notationOptions}
            value={values.notation}
            searchable={false}
            clearable={false}
            onChange={(option: MerlinSelectOption | null) =>
              option != null && onChange({ notation: option.value as Notation })
            }
            disabled={disabled}
          />
        </div>
      </div>
    </div>
    <div className="row model-options">
      <div className="col">
        <RSCheckbox
          label="Automatically generate creating and ending methods"
          checked={values.generateMethods}
          onChange={asRSChange<RSCheckboxChange>((value: string, checked: boolean) => onChange({ generateMethods: checked }))}
          disabled={disabled}
        />
        <RSCheckbox
          label="Propagate automatically when creating a dependency"
          checked={values.propagateOnDependency}
          onChange={asRSChange<RSCheckboxChange>((value: string, checked: boolean) => onChange({ propagateOnDependency: checked }))}
          disabled={disabled}
        />
        <RSCheckbox
          label="Propagate automatically when create a method"
          checked={values.propagateOnMethod}
          onChange={asRSChange<RSCheckboxChange>((value: string, checked: boolean) => onChange({ propagateOnMethod: checked }))}
          disabled={disabled}
        />
      </div>
    </div>
  </React.Fragment>
);

export default ModelSettingsForm;
